import { motion, useInView } from 'framer-motion';
import { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { MapPin, Users, Trophy, Send, Globe } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import NumberFlow from '@number-flow/react';
import { CardCorner, TranslatedText } from '../ui';
import { useLandingContent } from '../../hooks/useLandingContent';

const ICON_MAP: Record<string, LucideIcon> = { MapPin, Users, Trophy, Send, Globe };

interface StatItem {
  icon: LucideIcon;
  value: number;
  key: string;
  suffix: string;
  labelEs?: string | null;
  labelEn?: string | null;
}

const FALLBACK_STATS: StatItem[] = [
  { icon: MapPin, value: 6, key: 'countries', suffix: '' },
  { icon: Users, value: 29, key: 'teams', suffix: '+' },
  { icon: Trophy, value: 30, key: 'players', suffix: '+' },
  { icon: Send, value: 500, key: 'applications', suffix: '+' },
];

export function Stats() {
  const { t, i18n } = useTranslation();
  const ref = useRef<HTMLDivElement>(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });
  const { data } = useLandingContent();
  const isSpanish = i18n.language === 'es';

  const stats: StatItem[] = data?.stats?.length
    ? data.stats.map((s) => ({
        icon: ICON_MAP[s.icon] ?? Globe,
        value: s.value,
        key: s.key,
        suffix: s.suffix,
        labelEs: s.label_es,
        labelEn: s.label_en,
      }))
    : FALLBACK_STATS;

  return (
    <section ref={ref} className="relative py-20 md:py-28 overflow-hidden bg-transparent">
      {/* Semi-transparent overlay for readability */}
      <div className="absolute inset-0 bg-brand-darkNavy/60 backdrop-blur-sm" />
      
      {/* Glow effects */}
      <div className="absolute top-0 left-1/4 w-[400px] h-[400px] bg-brand-yellow/10 rounded-full blur-[100px]" />

      <div className="relative z-10 container mx-auto px-4 sm:px-6 lg:px-8">
        {/* Title */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <motion.div 
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-brand-yellow/10 border border-brand-yellow/30 mb-6"
            whileHover={{ scale: 1.05 }}
          >
            <Globe className="w-4 h-4 text-brand-yellow" />
            <span className="text-sm font-bold text-brand-yellow uppercase tracking-wider">{t('stats.badge')}</span>
          </motion.div>
          
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-black text-white">
            <TranslatedText 
              i18nKey="stats.title" 
              type="fadeLetters"
              duration={0.3}
              stagger={0.02}
            />
          </h2>
        </motion.div>

        {/* Stats Grid - FIFA Ultimate Team Style */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
          {stats.map((stat, index) => (
            <motion.div
              key={stat.key}
              initial={{ opacity: 0, y: 30, rotateX: -15 }}
              whileInView={{ opacity: 1, y: 0, rotateX: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              whileHover={{ scale: 1.05, y: -5 }}
              className="group perspective-1000"
            >
              <div className="relative bg-brand-navy/80 backdrop-blur-xl border border-brand-yellow/20 rounded-2xl p-6 overflow-hidden transition-all duration-300 group-hover:border-brand-yellow/50">
                {/* Corner decorations */}
                <CardCorner position="top-left" />
                <CardCorner position="bottom-right" />
                
                {/* Background number */}
                <div className="absolute -right-4 -bottom-4 text-[80px] font-black text-brand-yellow/5 leading-none pointer-events-none select-none">
                  {stat.value}
                </div>
                
                {/* Icon */}
                <div className="relative z-10 flex justify-center mb-4">
                  <motion.div
                    className="w-14 h-14 rounded-xl bg-brand-yellow/10 flex items-center justify-center group-hover:bg-brand-yellow/20 transition-colors"
                    whileHover={{ rotate: 360 }}
                    transition={{ duration: 0.6 }}
                  >
                    <stat.icon className="w-7 h-7 text-brand-yellow" />
                  </motion.div>
                </div>
                
                {/* Value with NumberFlow */}
                <div className="relative z-10 text-4xl md:text-5xl font-black text-brand-yellow text-center mb-2">
                  <NumberFlow 
                    value={isInView ? stat.value : 0}
                    format={{ notation: 'standard' }}
                    transformTiming={{ duration: 1000, easing: 'ease-out' }}
                    spinTiming={{ duration: 1000, easing: 'ease-out' }}
                  />
                  {stat.suffix && <span>{stat.suffix}</span>}
                </div>
                
                {/* Label */}
                <div className="relative z-10 text-sm text-dark-300 text-center font-medium">
                  {stat.labelEs || stat.labelEn ? (
                    <span>{(isSpanish ? stat.labelEs : stat.labelEn) || stat.labelEs || stat.labelEn}</span>
                  ) : (
                    <TranslatedText 
                      i18nKey={`stats.${stat.key}`}
                      type="fade"
                      duration={0.4}
                    />
                  )}
                </div>
                
                {/* Hover glow */}
                <div className="absolute inset-0 bg-brand-yellow/5 opacity-0 group-hover:opacity-100 transition-opacity" />
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
